/**
 * POST a FormData with upload-progress events.
 *
 * This is a raw XMLHttpRequest deliberately, and it is the one reason the uploader
 * doesn't use XHRFetcher/JsonFetcher: `fetch` cannot report request-body upload
 * progress, and a person on mobile data pushing a photo needs to see it moving.
 *
 * Extracted from the crew FileUploader's upload manager (which reached into the live
 * component for its endpoint and method). Crew's manager is now an adapter over this.
 */

/**
 * @param {string} url - Absolute URL. On a native-app page (a `campingnz://` custom
 *   scheme document) a RELATIVE url is intercepted by the app and arrives cookieless,
 *   so callers there must pass an absolute https URL.
 * @param {FormData} formData
 * @param {Object} options
 * @param {string} [options.method='POST']
 * @param {Function} [options.onProgress] - Called with a whole-request percentage 0-100.
 * @param {boolean} [options.withCredentials=true] - Send cookies cross-origin, so the
 *   `itchi` session cookie attributes the submission to the signed-in person.
 * @param {Object} [options.headers] - Extra request headers. Keep this empty for
 *   cross-origin posts unless the header is on the server's CORS allow-list — an
 *   unlisted header turns a simple multipart POST into a preflighted one.
 * @returns {Promise<Response>}
 */
export function uploadFormData(url, formData, options = {}) {
    const {
        method = 'POST',
        onProgress,
        withCredentials = true,
        headers = {}
    } = options;

    return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();

        xhr.upload.addEventListener('progress', (event) => {
            if (!event.lengthComputable || !onProgress) return;
            onProgress(Math.round((event.loaded / event.total) * 100));
        });

        xhr.addEventListener('load', () => {
            resolve(new Response(xhr.response, {
                status: xhr.status,
                statusText: xhr.statusText,
                headers: parseHeaders(xhr.getAllResponseHeaders())
            }));
        });

        xhr.addEventListener('error', () => reject(new Error('Network error occurred')));
        xhr.addEventListener('abort', () => reject(new Error('Upload aborted')));

        xhr.open(method, url);
        xhr.withCredentials = withCredentials;

        Object.keys(headers).forEach((key) => {
            if (headers[key]) xhr.setRequestHeader(key, headers[key]);
        });

        xhr.send(formData);
    });
}

/**
 * Rebuild a Headers object from XMLHttpRequest's raw header string.
 * @param {string} headerString
 * @returns {Headers}
 */
export function parseHeaders(headerString) {
    const headers = new Headers();
    const lines = (headerString || '').trim().split(/[\r\n]+/);

    lines.forEach(line => {
        if (!line) return;
        const parts = line.split(': ');
        const key = parts.shift();
        const value = parts.join(': ');
        if (key) headers.append(key, value);
    });

    return headers;
}
